'use client';

import React, { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import Header from '@/components/common/Header';

const ImprintPage = () => {
  const { t, language } = useLanguage();
  const [activeLanguage, setActiveLanguage] = useState<'en' | 'de'>(language as 'en' | 'de');

  const imprintContent = {
    en: {
      title: 'Imprint',
      subtitle: 'Legal Disclosure',
      sections: {
        company: {
          title: 'Company Information',
          content: [
            { label: 'Company Name', value: 'Nolens Volens GmbH' },
            { label: 'Trade Name', value: 'TravelHeroes' },
            { label: 'Managing Director', value: 'Nikolaus Dorfstetter' },
            { label: 'Address', value: 'Obere Donaustraße 62, 1020 Vienna, Austria' },
          ],
        },
        legal: {
          title: 'Legal & Registration',
          content: [
            { label: 'VAT ID', value: 'ATU80077845' },
            { label: 'Company Register Number', value: 'FN 613030 s' },
            { label: 'Registration Court', value: 'Handelsgericht Wien' },
          ],
        },
        contact: {
          title: 'Contact Information',
          content: [
            { label: 'Email', value: 'support@travelheroes.at' },
          ],
        },
        regulatory: {
          title: 'Regulatory Information',
          content: [
            {
              label: 'Business Purpose',
              value:
                'Mobile marketplace connecting travelers with local business offers, discount and promotional platform, user redemption tracking system.',
            },
            {
              label: 'Professional Regulatory Authority',
              value: 'Wirtschaftskammer Wien',
            },
            {
              label: 'Applicable Regulations',
              value: 'Austrian E-Commerce Act (ECG), Austrian Media Law (Mediengesetz)',
            },
          ],
        },
        disclaimer: {
          title: 'Content Disclaimer',
          content: [
            {
              label: 'Content Responsibility',
              value:
                'The contents of our pages were created with great care. However, we cannot guarantee the accuracy, completeness, or timeliness of the content. As a service provider, we are responsible for our own content on these pages under general law. However, we are not obligated to monitor transmitted or stored third-party information or to investigate circumstances that indicate illegal activity.',
            },
            {
              label: 'External Links',
              value:
                'Our website contains links to external websites over which we have no control. Therefore, we cannot accept any responsibility for their content. The respective provider or operator of the pages is always responsible for the content of the linked pages.',
            },
          ],
        },
      },
    },
    de: {
      title: 'Impressum',
      subtitle: 'Rechtliche Offenlegung',
      sections: {
        company: {
          title: 'Unternehmensinformationen',
          content: [
            { label: 'Firmenname', value: 'Nolens Volens GmbH' },
            { label: 'Handelsname', value: 'TravelHeroes' },
            { label: 'Geschäftsführer', value: 'Nikolaus Dorfstetter' },
            { label: 'Adresse', value: 'Obere Donaustraße 62, 1020 Wien, Österreich' },
          ],
        },
        legal: {
          title: 'Rechtliches & Registrierung',
          content: [
            { label: 'UID-Nummer', value: 'ATU80077845' },
            { label: 'Firmenbuchnummer', value: 'FN 613030 s' },
            { label: 'Firmenbuchgericht', value: 'Handelsgericht Wien' },
          ],
        },
        contact: {
          title: 'Kontaktinformationen',
          content: [
            { label: 'E-Mail', value: 'support@travelheroes.at' },
          ],
        },
        regulatory: {
          title: 'Aufsichtsbehörde',
          content: [
            {
              label: 'Unternehmensgegenstand',
              value:
                'Mobile Plattform zur Verbindung von Reisenden mit lokalen Geschäftsangeboten, Rabatt- und Aktionsplattform, Einlösungsverfolgungssystem für Nutzer.',
            },
            {
              label: 'Berufliche Aufsichtsbehörde',
              value: 'Wirtschaftskammer Wien',
            },
            {
              label: 'Anwendbare Vorschriften',
              value: 'Österreichisches E-Commerce-Gesetz (ECG), Österreichisches Mediengesetz',
            },
          ],
        },
        disclaimer: {
          title: 'Haftungsausschluss',
          content: [
            {
              label: 'Inhaltsverantwortung',
              value:
                'Die Inhalte unserer Seiten wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen. Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Wir sind jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen.',
            },
            {
              label: 'Externe Links',
              value:
                'Unser Angebot enthält Links zu externen Webseiten Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.',
            },
          ],
        },
      },
    },
  };

  const content = imprintContent[activeLanguage];

  const renderSection = (section: {
    title: string;
    content: Array<{ label: string; value: string }>;
  }) => (
    <div data-component-id="src/app/imprint/page.tsx:144:4" data-component-path="src/app/imprint/page.tsx" data-component-line="144" data-component-end-line="156" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-8%22%7D" className="mb-8">
      <h2 data-component-id="src/app/imprint/page.tsx:145:6" data-component-path="src/app/imprint/page.tsx" data-component-line="145" data-component-end-line="147" data-component-file="page.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-2xl%20font-headline%20font-bold%20text-foreground%20mb-4%22%7D" className="text-2xl font-headline font-bold text-foreground mb-4">
        {section?.title}
      </h2>
      <div data-component-id="src/app/imprint/page.tsx:148:6" data-component-path="src/app/imprint/page.tsx" data-component-line="148" data-component-end-line="155" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-3%22%7D" className="space-y-3">
        {section?.content?.map((item, index) => (
          <div data-component-id="src/app/imprint/page.tsx:150:10" data-component-path="src/app/imprint/page.tsx" data-component-line="150" data-component-end-line="153" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-3%20gap-2%22%7D" key={index} className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <dt data-component-id="src/app/imprint/page.tsx:151:12" data-component-path="src/app/imprint/page.tsx" data-component-line="151" data-component-end-line="151" data-component-file="page.tsx" data-component-name="dt" data-component-content="%7B%22elementName%22%3A%22dt%22%2C%22className%22%3A%22font-body%20font-semibold%20text-foreground%22%2C%22textContent%22%3A%22%3A%22%7D" className="font-body font-semibold text-foreground">{item?.label}:</dt>
            <dd data-component-id="src/app/imprint/page.tsx:152:12" data-component-path="src/app/imprint/page.tsx" data-component-line="152" data-component-end-line="152" data-component-file="page.tsx" data-component-name="dd" data-component-content="%7B%22elementName%22%3A%22dd%22%2C%22className%22%3A%22md%3Acol-span-2%20font-body%20text-muted-foreground%22%7D" className="md:col-span-2 font-body text-muted-foreground">{item?.value}</dd>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div data-component-id="src/app/imprint/page.tsx:160:4" data-component-path="src/app/imprint/page.tsx" data-component-line="160" data-component-end-line="210" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <Header data-component-id="src/app/imprint/page.tsx:161:6" data-component-path="src/app/imprint/page.tsx" data-component-line="161" data-component-end-line="161" data-component-file="page.tsx" data-component-name="Header" data-component-content="%7B%22elementName%22%3A%22Header%22%7D" />
      
      <main data-component-id="src/app/imprint/page.tsx:163:6" data-component-path="src/app/imprint/page.tsx" data-component-line="163" data-component-end-line="209" data-component-file="page.tsx" data-component-name="main" data-component-content="%7B%22elementName%22%3A%22main%22%2C%22className%22%3A%22container%20mx-auto%20px-4%20py-16%22%7D" className="container mx-auto px-4 py-16">
        <div data-component-id="src/app/imprint/page.tsx:164:8" data-component-path="src/app/imprint/page.tsx" data-component-line="164" data-component-end-line="208" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-4xl%20mx-auto%22%7D" className="max-w-4xl mx-auto">
          <div data-component-id="src/app/imprint/page.tsx:165:10" data-component-path="src/app/imprint/page.tsx" data-component-line="165" data-component-end-line="193" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-between%20items-center%20mb-8%22%7D" className="flex justify-between items-center mb-8">
            <div data-component-id="src/app/imprint/page.tsx:166:12" data-component-path="src/app/imprint/page.tsx" data-component-line="166" data-component-end-line="173" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
              <h1 data-component-id="src/app/imprint/page.tsx:167:14" data-component-path="src/app/imprint/page.tsx" data-component-line="167" data-component-end-line="169" data-component-file="page.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-4xl%20font-headline%20font-bold%20text-foreground%20mb-2%22%7D" className="text-4xl font-headline font-bold text-foreground mb-2">
                {content?.title}
              </h1>
              <p data-component-id="src/app/imprint/page.tsx:170:14" data-component-path="src/app/imprint/page.tsx" data-component-line="170" data-component-end-line="172" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20font-body%20text-muted-foreground%22%7D" className="text-lg font-body text-muted-foreground">
                {content?.subtitle}
              </p>
            </div>
            
            <div data-component-id="src/app/imprint/page.tsx:175:12" data-component-path="src/app/imprint/page.tsx" data-component-line="175" data-component-end-line="192" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20gap-2%22%7D" className="flex gap-2">
              <button data-component-id="src/app/imprint/page.tsx:176:14" data-component-path="src/app/imprint/page.tsx" data-component-line="176" data-component-end-line="183" data-component-file="page.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22textContent%22%3A%22English%22%7D"
                onClick={() => setActiveLanguage('en')}
                className={`px-4 py-2 rounded-lg font-body transition-colors ${
                  activeLanguage === 'en' ?'bg-primary text-primary-foreground' :'bg-card text-muted-foreground hover:bg-muted'
                }`}
              >
                English
              </button>
              <button data-component-id="src/app/imprint/page.tsx:184:14" data-component-path="src/app/imprint/page.tsx" data-component-line="184" data-component-end-line="191" data-component-file="page.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22textContent%22%3A%22Deutsch%22%7D"
                onClick={() => setActiveLanguage('de')}
                className={`px-4 py-2 rounded-lg font-body transition-colors ${
                  activeLanguage === 'de' ?'bg-primary text-primary-foreground' :'bg-card text-muted-foreground hover:bg-muted'
                }`}
              >
                Deutsch
              </button>
            </div>
          </div>

          <div data-component-id="src/app/imprint/page.tsx:195:10" data-component-path="src/app/imprint/page.tsx" data-component-line="195" data-component-end-line="207" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20border%20border-border%20rounded-lg%20p-8%20shadow-sm%22%7D" className="bg-card border border-border rounded-lg p-8 shadow-sm">
            {renderSection(content?.sections?.company)}
            {renderSection(content?.sections?.legal)}
            {renderSection(content?.sections?.contact)}
            {renderSection(content?.sections?.regulatory)}
            {renderSection(content?.sections?.disclaimer)}

            <div data-component-id="src/app/imprint/page.tsx:202:12" data-component-path="src/app/imprint/page.tsx" data-component-line="202" data-component-end-line="206" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-8%20pt-8%20border-t%20border-border%22%7D" className="mt-8 pt-8 border-t border-border">
              <p data-component-id="src/app/imprint/page.tsx:203:14" data-component-path="src/app/imprint/page.tsx" data-component-line="203" data-component-end-line="205" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20font-body%20text-muted-foreground%22%7D" className="text-sm font-body text-muted-foreground">
                {activeLanguage === 'en' ?'This imprint complies with Austrian E-Commerce Act (ECG) and Media Law (Mediengesetz) requirements.' :'Dieses Impressum entspricht den Anforderungen des österreichischen E-Commerce-Gesetzes (ECG) und Mediengesetzes.'}
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ImprintPage;